import { useState } from "react";

/**
 * Shared state for the "zero matching transaction found" acknowledgment flow (issue #498),
 * identical across PayableMatchSuggestionsModal and receivables' MarkAsReceivedModal: a
 * fetch-failure flag kept distinct from a genuinely empty result (never conflated — see
 * `.claude/rules/client-error-handling.md`'s Tier 2 pattern), the "None of these match" escape
 * hatch for a non-empty candidate list, and the single fixed-copy checkbox gating the
 * acknowledgment Confirm control.
 */
export function useZeroMatchAcknowledgment(
  /** Clears the caller's own candidate-selection state (a selected transaction id, or a selected
   * suggestion object) — differs per caller, so it stays a parameter rather than state owned
   * here. */
  clearSelection?: () => void,
) {
  // Distinct from `suggestions.length === 0` — see each modal's own finding-3 note.
  const [loadFailed, setLoadFailed] = useState(false);
  // Escape hatch (design spec AC6): candidates were shown, but the consultant ruled all of them
  // out. Transitions the view into the same acknowledgment flow used for zero candidates.
  const [noneOfTheseMatch, setNoneOfTheseMatch] = useState(false);
  // The single fixed-copy checkbox gating the acknowledgment Confirm control (AC4/AC5).
  const [acknowledged, setAcknowledged] = useState(false);

  const reset = () => {
    setLoadFailed(false);
    setNoneOfTheseMatch(false);
    setAcknowledged(false);
  };

  // Identical 3-line handler pair across every caller (issue #498) — see onNoneOfTheseMatch's
  // own clearSelection param for why only this part is a parameter.
  const onNoneOfTheseMatch = () => {
    clearSelection?.();
    setNoneOfTheseMatch(true);
  };
  const onBackToSuggestions = () => {
    setNoneOfTheseMatch(false);
    setAcknowledged(false);
  };

  return {
    loadFailed, setLoadFailed, noneOfTheseMatch, setNoneOfTheseMatch, acknowledged, setAcknowledged, reset,
    onNoneOfTheseMatch, onBackToSuggestions,
  };
}
